import React, { useState, useRef } from 'react';
import { Box, Button, Typography, CircularProgress, Alert, Paper } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useHistory, useLocation } from 'react-router-dom';
import { UploadService } from '../../services/dao/uploader-service';

const uploaderService = new UploadService();

export const UploadNewFindoc = () => {
    const history = useHistory();
    const location = useLocation();
    const projectId = new URLSearchParams(location.search).get('projectId');
    const fileRef = useRef(null);
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const handleFile = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        if (f.type !== 'application/pdf') { setError('Only PDF files are allowed.'); return; }
        setFile(f);
        setError('');
        setSuccess(false);
    };

    const handleUpload = async () => {
        if (!file) return;
        setLoading(true); setError('');
        const res = await uploaderService.uploadFile(file, 'findoc', projectId);
        setLoading(false);
        if (res?.error) setError(res.errorMessage || 'Upload failed');
        else if (projectId) history.push(`/buckets/${projectId}`);
        else { setSuccess(true); setFile(null); }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const f = e.dataTransfer.files[0];
        if (f) handleFile({ target: { files: [f] } });
    };

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 600, mx: 'auto' }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none' }}>
                Back
            </Button>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, background: '#E8EDF2',
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <DescriptionIcon sx={{ color: '#1F2F42', fontSize: '1.4rem' }} />
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Upload Statement</Typography>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, borderRadius: 2 }}
                action={<Button size="small" onClick={() => setSuccess(false)}>Upload Another</Button>}>
                Statement uploaded and processing!
            </Alert>}

            {!file && !success && (
                <Paper variant="outlined" sx={{ borderRadius: 3, border: '2px dashed rgba(0,0,0,0.12)', p: 6,
                    textAlign: 'center', cursor: 'pointer', transition: 'all 0.2s',
                    '&:hover': { borderColor: '#1F2F42', background: '#E8EDF2' } }}
                    onClick={() => fileRef.current?.click()}
                    onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
                    <input ref={fileRef} type="file" hidden accept=".pdf" onChange={handleFile} />
                    <CloudUploadIcon sx={{ fontSize: 48, color: '#1F2F42', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>Drop your statement here</Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>or click to browse · PDF only</Typography>
                </Paper>
            )}

            {file && (
                <Box>
                    <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DescriptionIcon sx={{ color: '#1F2F42', fontSize: '2rem' }} />
                        <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{file.name}</Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {(file.size / 1024).toFixed(0)} KB · PDF
                            </Typography>
                        </Box>
                    </Paper>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Button variant="outlined" fullWidth onClick={() => setFile(null)}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
                        <Button variant="contained" fullWidth onClick={handleUpload} disabled={loading}
                            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                            sx={{ borderRadius: 2, textTransform: 'none' }}>
                            {loading ? 'Uploading...' : 'Upload'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
